import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const styles = {
  global: `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif; background: linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%); min-height: 100vh; color: #3d4f4f; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes spin { to { transform: rotate(360deg); } }
  `,
  
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  
  card: {
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '48px 40px',
    maxWidth: '450px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(61, 79, 79, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    animation: 'fadeIn 0.6s ease-out',
  },
  
  icon: {
    fontSize: '64px',
    marginBottom: '24px',
  },
  
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#3d4f4f',
    marginBottom: '16px',
  },
  
  message: {
    fontSize: '16px',
    color: '#5a7070',
    lineHeight: '1.6',
    marginBottom: '32px',
  },
  
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid rgba(124, 152, 133, 0.2)',
    borderTopColor: '#7c9885',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 24px',
  },
  
  button: {
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    boxShadow: '0 4px 16px rgba(124, 152, 133, 0.3)',
  },
  
  buttonSecondary: {
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#5a7070',
    background: 'rgba(124, 152, 133, 0.15)',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    marginTop: '12px',
  },
  
  successIcon: {
    color: '#22c55e',
  },
  
  errorIcon: {
    color: '#ef4444',
  },
};

function EmailVerificationPage({ setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, no-token
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('no-token');
      setMessage('No verification token provided.');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await fetch(`${API_URL}/auth/verify-email?token=${token}`);
      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message);
        
        // If token provided, log the user in
        if (data.data?.token) {
          localStorage.setItem('token', data.data.token);
          setUser(data.data.user);
          
          // Redirect after 2 seconds
          setTimeout(() => {
            const redirectPath = data.data.user.signUpFinished 
              ? '/home' 
              : data.data.user.role === 'job_seeker' 
                ? '/createcv' 
                : '/createjoblisting';
            navigate(redirectPath);
          }, 2000);
        }
      } else {
        setStatus('error');
        setMessage(data.message || 'Failed to verify email.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setMessage('An error occurred. Please try again.');
    }
  };

  const handleGoToLogin = () => {
    navigate('/auth?mode=login');
  };

  const handleGoToRegister = () => {
    navigate('/auth?mode=register');
  };

  return (
    <>
      <style>{styles.global}</style>
      
      <div style={styles.container}>
        <div style={styles.card}>
          {status === 'verifying' && (
            <>
              <div style={styles.spinner}></div>
              <h1 style={styles.title}>Verifying your email...</h1>
              <p style={styles.message}>Please wait while we verify your email address.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ ...styles.icon, ...styles.successIcon }}>✓</div>
              <h1 style={styles.title}>Email Verified!</h1>
              <p style={styles.message}>{message}</p>
              <p style={{ ...styles.message, marginBottom: '16px' }}>Redirecting you to continue setup...</p>
              <button style={styles.button} onClick={handleGoToLogin}>
                Go to Login
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ ...styles.icon, ...styles.errorIcon }}>✕</div>
              <h1 style={styles.title}>Verification Failed</h1>
              <p style={styles.message}>{message}</p>
              <button style={styles.button} onClick={handleGoToLogin}>
                Go to Login
              </button>
              <button style={styles.buttonSecondary} onClick={handleGoToRegister}>
                Create New Account
              </button>
            </>
          )}

          {status === 'no-token' && (
            <>
              <div style={styles.icon}>🔗</div>
              <h1 style={styles.title}>Invalid Link</h1>
              <p style={styles.message}>{message}</p>
              <button style={styles.button} onClick={handleGoToLogin}>
                Go to Login
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default EmailVerificationPage;